import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Loader2, Droplet } from "lucide-react";
import AvatarViewer from "@/components/AvatarViewer";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import {
  MINI_MIRROR_IS_AVATURN_MODEL,
  MINI_MIRROR_SCALE,
  MINI_MIRROR_LOOK_AT_Y,
  MINI_MIRROR_CAMERA_DISTANCE,
  MINI_MIRROR_FRAMING,
} from "@/components/avatar/miniMirrorCalibration";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import { relativeTimeLabel } from "@/lib/relativeTime";
import { isQuotaError, parsePaymentErrorFromResponse, QuotaError } from "@/lib/quotaError";
import { usePaymentErrorHandler } from "@/hooks/usePaymentErrorHandler";
import { useIsMobile } from "@/hooks/use-mobile";
import { getAnimationUrl } from "@/config/avatarAnimations";
import { useVoiceInputWhisper } from "@/hooks/useVoiceInputWhisper";
import useVoice from "@/hooks/useVoice";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import { supabase } from "@/integrations/supabase/client";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { resolveAssistantMessageId } from "@/lib/report/resolveAssistantMessageId";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import { getOrCreateMirrorSession } from "@/lib/mirrorSession";
import { MiniMirrorLiveButton } from "@/components/common/MiniMirrorLiveButton";
import { shouldSkipTTS } from "@/lib/ttsFilters";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  useNutritionSnapshot,
  type NutritionTodaySummary,
  type NutritionWeekDaySummary,
  type NutritionHydrationDaySummary,
} from "@/hooks/useNutritionSnapshot";
import { isIOSNative } from "@/lib/platform";

/**
 * Nutrition hub Mini-Mirror — W6 premium refactor (ENERGIA hero).
 *
 * Mirrors W6 FitnessMiniMirror DNA (commit ed1746a / dba345e): self-contained
 * default export with NutritionMirrorProvider (mirror-chat SSE,
 * module_context: "nutrition") + AvatarSlot (frame TV gradient + radial cyan
 * halo + LIVE button + canonical avatar 1.6/1.7/2.5) + Hero (ENERGIA
 * composite score 0-100 with no-wrap clamp 28-32px) + 3 chips
 * (HOJE / SEMANA / HIDRATAÇÃO) opening Drawer with drill-down list +
 * VoiceSlot with breathing idle animation.
 *
 * Canonical outer card: linear-gradient #0a0e1a→#0f1419→#0a0e1a + ring-1
 * ring-white/5. Frozen avatar files NOT modified.
 *
 * Rename: "Charge" → ENERGIA/ENERGY/ENERGIE (Rafa decision 12/05). Composite
 * formula preserved (50%hyd + 30%prot + 20%cadence).
 */

const SILENT_MP3 = "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLmNvbQBURU5DAAAABwAAA0xhbWUAAAAAAAAAAAAAAP/7kGQAAANUMEoFPeACNQV40KEAAA0gwX4UPYACNgV5Io5eBAgAADXCHP/7kmRAAADSBHIJaxiADNgFhw2EABN0Fch2dYAI2gWHDYSAAAANPJy//+5JkwAAA0wRyHZ2AAjYBYcNhAAAA0gRyHZ1gAjaBYcNhIAAAAf/+5JkCgAA0gRyHZ1gAjaBYcNhAAAA0gRyHZ1gAjYBYcNhIAAAPf/7kmQAAADSBHIdnWACNoFhw2EgAADSBHIdnWACNgFhw2EgAAA=";

type VoiceState = "idle" | "listening" | "processing" | "speaking" | "error";
type DrawerKind = "today" | "week" | "hydration" | null;

interface NutritionMirrorContextValue {
  voiceState: VoiceState;
  lastResponse: string;
  // mirror_messages.id da resposta em `lastResponse`, resolvido depois do
  // stream (o SSE nao devolve id). null = ainda sem identidade estavel, e
  // entao o controle de denuncia nao aparece nesta resposta.
  lastResponseId: string | null;
  handleMicPress: () => Promise<void>;
  // Lip sync pass-through — consumed by AvatarSlot to drill AvatarCore's
  // useLipSync inputs. Mirrors the Agenda fix (12/05/26).
  audioElement: HTMLAudioElement | null;
  alignment: ElevenLabsAlignment | null;
}

const NutritionMirrorContext = createContext<NutritionMirrorContextValue | null>(null);

function useNutritionMirrorState(): NutritionMirrorContextValue {
  const ctx = useContext(NutritionMirrorContext);
  if (!ctx) {
    return {
      voiceState: "idle",
      lastResponse: "",
      // Fora do provider nao ha resposta e, portanto, nao ha id — o controle de
      // denuncia simplesmente nao aparece. Faltava aqui: o valor de fallback e o
      // que um consumidor mal montado recebe, e sem esta linha ele quebraria o
      // contrato do contexto em runtime, nao so no tipo.
      lastResponseId: null,
      handleMicPress: async () => {},
      audioElement: null,
      alignment: null,
    };
  }
  return ctx;
}

function NutritionMirrorProvider({ children }: { children: ReactNode }) {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const handlePaymentError = usePaymentErrorHandler();
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const whisper = useVoiceInputWhisper(lang);
  const tts = useVoice({ externalAudioElement: audioElementRef.current });

  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [lastResponse, setLastResponse] = useState<string>("");
  const [lastResponseId, setLastResponseId] = useState<string | null>(null);

  const resolveSessionId = useCallback(async (userId: string): Promise<string> => {
    return getOrCreateMirrorSession(userId);
  }, []);

  const runChatStream = useCallback(async (
    userMessage: string,
    userId: string,
    sessionId: string,
  ): Promise<string> => {
    const supaUrl = import.meta.env.VITE_SUPABASE_URL;
    const supaAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token || supaAnonKey;

    const res = await fetch(`${supaUrl}/functions/v1/mirror-chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
        "apikey": supaAnonKey,
      },
      body: JSON.stringify({
        promptType: "chat",
        userId,
        sessionId,
        userMessage,
        language: lang,
        mirrorTone: "balanced",
        module_context: "nutrition",
        // Apple 3.1.1 — tells mirror-chat to omit price + checkout URL on iOS native.
        platform: isIOSNative() ? "ios" : "web",
      }),
    });
    if (res.status === 402) {
      const pe = await parsePaymentErrorFromResponse(res);
      if (pe) throw new QuotaError(pe);
    }
    if (!res.ok || !res.body) throw new Error(`mirror-chat failed: ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = "";
    let finalText = "";
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let lineEnd: number;
      while ((lineEnd = buffer.indexOf("\n\n")) !== -1) {
        const chunk = buffer.slice(0, lineEnd);
        buffer = buffer.slice(lineEnd + 2);
        const eventLine = chunk.split("\n").find((l) => l.startsWith("event:"));
        const dataLine = chunk.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        const eventName = eventLine?.slice(6).trim();
        const dataStr = dataLine.slice(5).trim();
        try {
          const payload = JSON.parse(dataStr);
          if (eventName === "delta" && payload.text) accumulated += payload.text;
          if (eventName === "final" && payload.response) finalText = payload.response;
        } catch {
          // skip malformed event
        }
      }
    }
    return finalText || accumulated || "";
  }, [lang]);

  const handleMicPress = useCallback(async () => {
    const audioEl = audioElementRef.current;
    if (audioEl) {
      try {
        audioEl.src = SILENT_MP3;
        const p = audioEl.play();
        if (p && typeof p.then === "function") {
          p.then(() => audioEl.pause()).catch(() => { /* iOS blocked */ });
        }
      } catch { /* ignore */ }
    }

    if (voiceState === "listening") {
      setVoiceState("processing");
      try {
        const transcript = await whisper.stopListening();
        if (!transcript) {
          setVoiceState("idle");
          return;
        }
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        const sessionId = await resolveSessionId(user.id);
        const response = await runChatStream(transcript, user.id, sessionId);
        setLastResponse(response);
        // Denuncia: a resposta acabou de ser persistida pelo mirror-chat (o
        // insert precede o `final`), entao este SELECT devolve a linha certa.
        // Sem await no fluxo de voz — a fala nao espera o id.
        setLastResponseId(null);
        void resolveAssistantMessageId(sessionId, response).then(setLastResponseId);
        if (shouldSkipTTS(response)) {
          setVoiceState("idle");
          return;
        }
        setVoiceState("speaking");
        await tts.speak(response, { language: lang });
        setVoiceState("idle");
      } catch (err) {
        if (isQuotaError(err)) {
          handlePaymentError(err.payment);
          setVoiceState("idle");
          return;
        }
        console.error("[NutritionMiniMirror] voice loop failed:", err);
        setVoiceState("error");
        setTimeout(() => setVoiceState("idle"), 1500);
      }
      return;
    }

    if (voiceState !== "idle" && voiceState !== "error") return;
    setVoiceState("listening");
    try {
      await whisper.startListening();
    } catch (err) {
      console.error("[NutritionMiniMirror] startListening failed:", err);
      setVoiceState("error");
      setTimeout(() => setVoiceState("idle"), 1500);
    }
  }, [voiceState, whisper, tts, resolveSessionId, runChatStream, lang]);

  return (
    <NutritionMirrorContext.Provider value={{
      voiceState,
      lastResponse,
      lastResponseId,
      handleMicPress,
      audioElement: tts.audioElement,
      alignment: tts.alignment,
    }}>
      <audio ref={audioElementRef} playsInline preload="auto" className="sr-only" />
      {children}
    </NutritionMirrorContext.Provider>
  );
}

/* ── Avatar slot ──────────────────────────────────────────────────────── */

function AvatarSlot() {
  const { avatarUrl, loading: avatarLoading, hasAvatar } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície (bug 25/08). Predicado canônico: quem escolheu avatar sem ter
  // GLB próprio também cai na energia — nunca placeholder quebrado.
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;
  const isMobile = useIsMobile();
  void isMobile;
  // Lip sync wire (parity with Agenda 12/05/26): drill tts.audioElement +
  // tts.alignment from Provider so AvatarCore.useLipSync activates during
  // TTS playback. Without these props useLipSync stays disabled.
  const { audioElement, alignment } = useNutritionMirrorState();

  return (
    <div className="w-[200px] md:w-[280px] self-stretch shrink-0">
      <div
        className="relative w-full min-h-[220px] md:min-h-[300px] h-full rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #050811 0%, #0a0e1a 100%)",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 2px 8px rgba(0,0,0,0.4)",
        }}
      >
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background: "radial-gradient(circle at center, rgba(34,211,238,0.18) 0%, transparent 65%)",
          }}
        />
        <MiniMirrorLiveButton module="nutrition" />
        <div className="relative z-10 w-full h-full">
          {avatarLoading || surfaceLoading ? (
            <div className="w-full h-full" aria-hidden="true" />
          ) : !showAvatar ? (
            <VyvEnergy state="idle" ratio={0.5} interactive={false} />
          ) : (
            <AvatarViewer
              modelUrl={avatarUrl}
              height="100%"
              enableRotate={false}
              framing={MINI_MIRROR_FRAMING}
              isAvaturnModel={MINI_MIRROR_IS_AVATURN_MODEL}
              animationUrl={getAnimationUrl("home")}
              scaleOverride={MINI_MIRROR_SCALE}
              cameraDistanceOverride={MINI_MIRROR_CAMERA_DISTANCE}
              lookAtYOverride={MINI_MIRROR_LOOK_AT_Y}
              audioElement={audioElement}
              alignment={alignment}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hero + Chips ─────────────────────────────────────────────────────── */


function dayLabel(iso: string, lang: Lang): string {
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  const locale = localeTagFor(lang);
  return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
}

function energyScoreColor(score: number): string {
  if (score >= 70) return "rgba(34,211,238,0.95)";   // cyan-300
  if (score >= 40) return "rgba(253,224,71,0.95)";   // yellow-300
  return "rgba(252,165,165,0.90)";                    // red-300 dim
}

function hydrationLabelKey(pct: number): string {
  if (pct >= 75) return "nutrition.miniMirror.hydrationGood";
  if (pct >= 40) return "nutrition.miniMirror.hydrationMid";
  return "nutrition.miniMirror.hydrationLow";
}

function sourceI18nKey(source: string | null | undefined): string | null {
  if (!source) return null;
  const k = source.toLowerCase();
  if (k === "photo" || k === "manual" || k === "voice") {
    return `nutrition.miniMirror.source.${k}`;
  }
  return null;
}

function HeroAndChips() {
  const { t, tf, lang } = useLanguage();
  const { data, isLoading, error, refresh } = useNutritionSnapshot();
  const [drawer, setDrawer] = useState<DrawerKind>(null);

  const heroColor = useMemo(() => energyScoreColor(data.energyScore), [data.energyScore]);

  const subtitle = useMemo(() => {
    const labelKey = hydrationLabelKey(data.energyComponents.hydrationPct);
    const hyd = t(labelKey);
    const proteinLabel = t("nutrition.miniMirror.protein");
    return `${hyd} · ${data.todayMacros.protein}g ${proteinLabel}`;
  }, [data.energyComponents.hydrationPct, data.todayMacros.protein, t]);

  const meta = useMemo(() => {
    const lm = data.lastMeal;
    if (!lm) return t("nutrition.miniMirror.noMealsYet");
    const rel = relativeTimeLabel(lm.eaten_at, lang, tf);
    const lastLabel = t("nutrition.miniMirror.lastMeal");
    return `${lastLabel}: ${rel} · ${lm.kcal} kcal`;
  }, [data.lastMeal, lang, t, tf]);

  const sourceLabel = useMemo(() => {
    const lm = data.lastMeal;
    if (!lm || !lm.source) return "";
    const key = sourceI18nKey(lm.source);
    return key ? t(key) : lm.source.toUpperCase();
  }, [data.lastMeal, t]);

  if (error) return <div role="alert" className="flex-1 min-w-0 p-3 text-sm text-white/80"><p>{t("nutrition.error")}</p><button className="mt-2 min-h-11 underline" onClick={refresh}>{t("common.retry")}</button></div>;

  return (
    <div className="flex-1 min-w-0 flex flex-col justify-between">
      {/* HERO */}
      <div className="flex-1 min-w-0">
        <p
          className="font-display uppercase mb-2"
          style={{
            fontSize: "10px",
            letterSpacing: "0.25em",
            fontWeight: 500,
            color: "rgba(255,255,255,0.55)",
          }}
        >
          {t("nutrition.miniMirror.energyLabel")}
        </p>
        <div className="flex items-baseline leading-none min-w-0">
          <span
            className="font-display tabular-nums whitespace-nowrap"
            style={{
              fontSize: "clamp(28px, 7vw, 32px)",
              fontWeight: 200,
              letterSpacing: "-0.02em",
              color: heroColor,
              lineHeight: 1,
            }}
          >
            {isLoading ? "—" : data.energyScore}
          </span>
          <span
            className="font-display tabular-nums whitespace-nowrap ml-1"
            style={{
              fontSize: "clamp(20px, 5vw, 24px)",
              fontWeight: 200,
              color: "rgba(255,255,255,0.45)",
              lineHeight: 1,
            }}
          >
            /100
          </span>
        </div>
        <div
          className="rounded-full mt-2 mb-2"
          style={{ width: 140, maxWidth: "100%", height: 2, background: "rgba(34,211,238,0.25)" }}
        />
        <p
          className="line-clamp-2 w-full"
          style={{ fontSize: "14px", fontWeight: 500, color: "rgba(236,254,255,0.92)" }}
        >
          {subtitle}
        </p>
        <p
          className="mt-1 line-clamp-2 w-full"
          style={{ fontSize: "12px", fontWeight: 400, color: "rgba(255,255,255,0.55)" }}
        >
          {meta}
        </p>
        {sourceLabel && (
          <p
            className="mt-1.5 uppercase truncate"
            style={{ fontSize: "10px", letterSpacing: "0.08em", color: "rgba(255,255,255,0.45)" }}
          >
            {sourceLabel}
          </p>
        )}
      </div>

      {/* DIVIDER */}
      <div className="mt-3 mb-3" style={{ height: 0.5, background: "rgba(255,255,255,0.06)" }} />

      {/* CHIPS */}
      <div className="flex gap-1.5">
        {[
          { kind: "today" as const, count: data.todayMealsCount, label: t("nutrition.miniMirror.todayChip") },
          { kind: "week" as const, count: data.weekMealsCount, label: t("nutrition.miniMirror.weekChip") },
          { kind: "hydration" as const, count: data.todayHydrationCups, label: t("nutrition.miniMirror.hydrationChip") },
        ].map((c) => (
          <button
            key={c.kind}
            type="button"
            onClick={() => setDrawer(c.kind)}
            className="flex-1 min-w-0 flex flex-col items-center justify-center rounded-xl transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] cursor-pointer px-2 py-2"
            style={{
              minHeight: 46,
              background: "rgba(255,255,255,0.04)",
              border: "0.5px solid rgba(255,255,255,0.08)",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)"; }}
            aria-label={`${c.label}: ${c.count}`}
          >
            <span
              className="font-display tabular-nums"
              style={{ fontSize: 18, fontWeight: 200, color: "rgba(236,254,255,0.95)", lineHeight: 1 }}
            >
              {isLoading ? "—" : c.count}
            </span>
            <span
              className="font-display uppercase mt-0.5 truncate max-w-full"
              style={{ fontSize: 10, letterSpacing: "0.10em", fontWeight: 500, color: "rgba(255,255,255,0.55)" }}
            >
              {c.label}
            </span>
          </button>
        ))}
      </div>

      {/* DRAWER */}
      <Drawer open={drawer !== null} onOpenChange={(open) => { if (!open) setDrawer(null); }}>
        <DrawerContent
          className="rounded-t-3xl"
          style={{
            background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)",
            border: "none",
          }}
        >
          <DrawerHeader>
            <DrawerTitle className="text-white text-base font-display tracking-wide uppercase">
              {drawer === "today" && t("nutrition.miniMirror.drawerTodayTitle")}
              {drawer === "week" && t("nutrition.miniMirror.drawerWeekTitle")}
              {drawer === "hydration" && t("nutrition.miniMirror.drawerHydrationTitle")}
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto">
            {drawer === "today" && <TodayMealsView meals={data.chips.today} />}
            {drawer === "week" && <WeekSummaryView days={data.chips.week} />}
            {drawer === "hydration" && <HydrationBarChartView days={data.chips.hydration} />}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

/* ── Drawer subviews ──────────────────────────────────────────────────── */

function mealTypeEmoji(mealType: string | null | undefined): string {
  switch ((mealType ?? "").toLowerCase()) {
    case "breakfast": return "🍳";
    case "lunch": return "🍽️";
    case "snack": return "☕";
    case "dinner": return "🌙";
    case "pre_workout":
    case "post_workout": return "🏋️";
    default: return "🍴";
  }
}

function TodayMealsView({ meals }: { meals: NutritionTodaySummary[] }) {
  const { t, tf, lang } = useLanguage();
  if (!meals || meals.length === 0) {
    return <p className="text-center text-white/50 py-8 text-sm">{t("nutrition.miniMirror.noMealsYet")}</p>;
  }
  return (
    <div className="space-y-2">
      {meals.map((m) => (
        <div
          key={m.id}
          className="flex items-center gap-3 rounded-xl p-3"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)" }}
        >
          <div
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-base"
            style={{ background: "rgba(34,211,238,0.10)", border: "0.5px solid rgba(34,211,238,0.20)" }}
          >
            <span aria-hidden="true">{mealTypeEmoji(m.meal_type)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[14px] font-medium truncate">
              {m.description ?? "—"}
            </p>
            <p className="text-white/45 text-[11px] mt-0.5 tabular-nums">
              {m.kcal} kcal{m.source ? ` · ${m.source.toUpperCase()}` : ""}
            </p>
          </div>
          <span className="text-white/45 text-[11px] shrink-0 tabular-nums">
            {relativeTimeLabel(m.created_at, lang, tf)}
          </span>
        </div>
      ))}
    </div>
  );
}

function WeekSummaryView({ days }: { days: NutritionWeekDaySummary[] }) {
  const { t, lang } = useLanguage();
  if (!days || days.length === 0) {
    return <p className="text-center text-white/50 py-8 text-sm">{t("nutrition.miniMirror.noMealsYet")}</p>;
  }
  return (
    <div className="space-y-2">
      {days.map((d) => (
        <div
          key={d.day}
          className="flex items-center gap-3 rounded-xl p-3"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)" }}
        >
          <div
            className="font-display shrink-0 text-center"
            style={{ width: 80, fontSize: 11, fontWeight: 500, color: "rgba(34,211,238,0.85)", letterSpacing: "0.06em" }}
          >
            {dayLabel(d.day, lang)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[13px] font-medium tabular-nums">
              {d.meals_count} {d.meals_count === 1 ? "ref" : "refs"} · {d.kcal_total} kcal
            </p>
            <p className="text-white/45 text-[11px] mt-0.5 tabular-nums">
              {d.protein_total}g {t("nutrition.miniMirror.protein")}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function HydrationBarChartView({ days }: { days: NutritionHydrationDaySummary[] }) {
  const { t, lang } = useLanguage();
  if (!days || days.length === 0) {
    return <p className="text-center text-white/50 py-8 text-sm">{t("nutrition.miniMirror.noMealsYet")}</p>;
  }
  // Reverse to oldest-first for visual left-to-right reading order
  const ordered = [...days].reverse();
  const barColor = (pct: number) => {
    if (pct >= 75) return "rgba(34,211,238,0.85)";
    if (pct >= 40) return "rgba(253,224,71,0.85)";
    return "rgba(252,165,165,0.80)";
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-2 h-40 px-2 pt-2">
        {ordered.map((d) => {
          const pctClamped = Math.max(4, Math.min(100, d.pct));
          return (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
              <span className="text-[10px] tabular-nums text-white/60">{d.cups}</span>
              <div
                className="w-full rounded-md transition-all"
                style={{
                  height: `${pctClamped}%`,
                  background: barColor(d.pct),
                  minHeight: 4,
                }}
                aria-label={`${dayLabel(d.day, lang)}: ${d.cups}/${d.goal}`}
              />
              <span className="text-[9px] uppercase font-display text-white/45 tracking-wide truncate max-w-full">
                {dayLabel(d.day, lang).split(",")[0] ?? d.day.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center justify-center gap-2 text-white/55 text-[11px]">
        <Droplet size={12} className="text-cyan-300/70" />
        <span>
          {ordered[ordered.length - 1]?.cups ?? 0} / {ordered[ordered.length - 1]?.goal ?? 8}
        </span>
      </div>
    </div>
  );
}

/* ── Voice slot ───────────────────────────────────────────────────────── */

function VoiceSlot() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { voiceState, lastResponse, lastResponseId, handleMicPress } = useNutritionMirrorState();
  const { isReported } = useReportedContent();

  const micLabel = (() => {
    if (voiceState === "listening") return t("nutrition.mirror.listening");
    if (voiceState === "processing") return t("nutrition.mirror.processing");
    if (voiceState === "error") return t("nutrition.mirror.error");
    return t("nutrition.mirror.tapToTalk");
  })();

  const micClass = (() => {
    if (voiceState === "listening") return "bg-cyan-500 ring-2 ring-cyan-300 animate-pulse scale-110 shadow-[0_0_28px_rgba(34,211,238,0.6)]";
    if (voiceState === "processing") return "bg-cyan-500/50 ring-2 ring-cyan-400/50 shadow-[0_0_24px_rgba(34,211,238,0.35)]";
    if (voiceState === "speaking") return "bg-cyan-500/60 ring-2 ring-cyan-300/60 shadow-[0_0_24px_rgba(34,211,238,0.45)]";
    if (voiceState === "error") return "bg-red-500/60 ring-2 ring-red-300/60";
    return "bg-cyan-500/70 ring-2 ring-cyan-400/60 hover:bg-cyan-500 nutrition-mm-idle-breathe";
  })();

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      <style>{`
        @keyframes nutrition-mm-idle-breathe {
          0%, 100% { transform: scale(1); box-shadow: 0 0 18px rgba(34,211,238,0.40); }
          50%      { transform: scale(1.03); box-shadow: 0 0 28px rgba(34,211,238,0.20); }
        }
        .nutrition-mm-idle-breathe { animation: nutrition-mm-idle-breathe 2.5s ease-in-out infinite; }
      `}</style>
      <button
        type="button"
        onClick={handleMicPress}
        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${micClass}`}
        aria-label={micLabel}
        title={micLabel}
      >
        {voiceState === "processing" ? (
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        ) : (
          <Mic className="w-6 h-6 text-white" />
        )}
      </button>

      <p className="text-[10px] uppercase tracking-widest text-foreground/40">{micLabel}</p>

      {lastResponse && !isReported(REPORT_SURFACES.MIRROR_MINI, lastResponseId) && (
        <div className="max-w-md text-center transition-opacity duration-200 px-4">
          <p className="text-sm text-foreground/85 line-clamp-2 leading-snug">{lastResponse}</p>
          <div className="mt-1 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => navigate("/mirror")}
            className="text-[11px] text-cyan-300 hover:text-cyan-200"
          >
            {t("nutrition.mirror.seeMore")}
          </button>
          {/* Denuncia da resposta do MiniMirror. Mesma pipeline do Espelho
              principal (mirror-chat), mesma tabela — logo, mesma superficie de
              denuncia. So aparece quando o id da linha ja foi resolvido. */}
          {lastResponseId && (
            <ReportContentButton
              surface={REPORT_SURFACES.MIRROR_MINI}
              contentId={lastResponseId}
              snapshot={lastResponse}
              context={{ module: "nutrition" }}
            />
          )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Self-contained default export ────────────────────────────────────── */

export default function NutritionMiniMirror() {
  return (
    <NutritionMirrorProvider>
      <div
        className="rounded-2xl p-5 ring-1 ring-white/5"
        style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)" }}
      >
        <div className="flex items-stretch gap-4">
          <AvatarSlot />
          <HeroAndChips />
        </div>
        <div className="mt-5 pt-4 border-t border-white/5">
          <VoiceSlot />
        </div>
      </div>
    </NutritionMirrorProvider>
  );
}
